import { useCallback, useEffect, useState } from 'react';
import { App } from 'antd';
import PackageService from 'services/package';
import ImageService from 'services/image';

const service = new PackageService();
const imageService = new ImageService();

const TIER_ORDER = { mien_phi: 0, mini: 1, pro: 2, vip: 3 };

const useComponent = () => {
  const { message, modal } = App.useApp();

  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(null);
  const [packages, setPackages] = useState([]);

  // upgrade request flow
  const [upgradeStep, setUpgradeStep] = useState(null); // null | 'select' | 'payment' | 'done'
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(1);
  const [upgradeRequest, setUpgradeRequest] = useState(null);
  const [paymentNote, setPaymentNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [curr, pkgs] = await Promise.all([
        service.getCurrent(),
        service.listPackages(),
      ]);
      setCurrent(curr);
      setPackages(pkgs || []);
    } catch (e) {
      message.error(e?.message || 'Không thể tải thông tin gói cước');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const currentTierOrder = TIER_ORDER[current?.tier] ?? 0;

  const onClickUpgrade = (pkg) => {
    setSelectedPackage(pkg);
    setSelectedDuration(1);
    setUpgradeStep('select');
  };

  const onCancelUpgrade = () => {
    setUpgradeStep(null);
    setSelectedPackage(null);
    setSelectedDuration(1);
    setUpgradeRequest(null);
    setPaymentNote('');
  };

  const onConfirmSelect = async () => {
    setSubmitting(true);
    try {
      const result = await service.createUpgradeRequest({
        package_key: selectedPackage.key,
        duration_months: selectedDuration,
      });
      setUpgradeRequest(result);
      setUpgradeStep('payment');
    } catch (e) {
      message.error(e?.message || 'Không thể tạo yêu cầu nâng cấp');
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirmPayment = async () => {
    if (!upgradeRequest?.request?.id) return;
    setSubmitting(true);
    try {
      await service.confirmPayment({
        request_id: upgradeRequest.request.id,
        payment_note: paymentNote,
      });
      setUpgradeStep('done');
    } catch (e) {
      message.error(e?.message || 'Xác nhận thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  const getUsagePercent = (used, limit) => {
    if (!limit) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  };

  const getDaysLeft = () => {
    const expiry = current?.subscription?.expiry_on;
    if (!expiry) return null;
    const diff = new Date(expiry) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const pricing = (() => {
    if (!selectedPackage) return null;
    const base = selectedPackage.price_monthly * selectedDuration;
    const bonus = selectedDuration === 6 && !selectedPackage.is_free ? 1 : 0;
    return { amount: base, bonus_months: bonus, total_months: selectedDuration + bonus };
  })();

  // Storage manager (orphan/soft-deleted files awaiting hard purge)
  const [storageManagerOpen, setStorageManagerOpen] = useState(false);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [orphans, setOrphans] = useState([]);
  const [purging, setPurging] = useState(false);

  const loadOrphans = useCallback(async () => {
    setOrphanLoading(true);
    try {
      const list = await imageService.orphanList();
      setOrphans(Array.isArray(list) ? list : []);
    } catch (e) {
      message.error(e?.message || 'Không thể tải danh sách file đã xoá');
    } finally {
      setOrphanLoading(false);
    }
  }, []);

  const openStorageManager = async () => {
    setStorageManagerOpen(true);
    await loadOrphans();
  };
  const closeStorageManager = () => setStorageManagerOpen(false);

  const purgeOrphans = async (ids) => {
    if (!ids || ids.length === 0) return;
    setPurging(true);
    try {
      await imageService.orphanPurge(ids);
      message.success(`Đã xoá vĩnh viễn ${ids.length} file`);
      await loadOrphans();
      await loadData();
    } catch (e) {
      message.error(e?.message || 'Không thể xoá');
    } finally {
      setPurging(false);
    }
  };

  return {
    loading,
    current,
    packages,
    currentTierOrder,
    upgradeStep,
    selectedPackage,
    selectedDuration,
    upgradeRequest,
    paymentNote,
    submitting,
    pricing,
    onClickUpgrade,
    onCancelUpgrade,
    onConfirmSelect,
    onConfirmPayment,
    setSelectedDuration,
    setPaymentNote,
    getUsagePercent,
    getDaysLeft,
    TIER_ORDER,
    storageManagerOpen,
    openStorageManager,
    closeStorageManager,
    orphans,
    orphanLoading,
    purging,
    purgeOrphans,
    loadOrphans,
  };
};

export default useComponent;
